import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from '../../pages/admin/AdminHeader';
import {
  getTextileProducts,
  getTextileProduct,
  deleteTextileProduct,
  bulkDeleteTextileProducts,
  getProductsCreatedByStats,
  downloadProductsCreatedByExcel,
  downloadProductsPdf,
  getStorageUsage
} from '../../services/productService';
import { mediaKey, mediaUrl } from '../../utils/mediaUrl';
import '../../css/AdminProductPage.css';
import {
  FileText,
  Download,
  Search,
  X,
  Plus,
  Trash2,
  Eye,
  Pencil,
  User,
  Loader2,
  Package,
  AlertTriangle,
  ArrowRight,
  HardDrive
} from "lucide-react";
import { Table } from '../../components/Table';
import { useToast } from '../../contexts/ToastContext';

// Standard apparel size hierarchy — XS must always precede S!
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL', 'FREE SIZE'];

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const digits = value >= 10 || i === 0 ? 1 : 2;
  return `${value.toFixed(digits)} ${units[i]}`;
};

const productMediaBytes = (product, files) => {
  if (!files) return null;
  const seen = new Set();
  let total = 0;
  let matched = 0;
  (product?.images || []).forEach((img) => {
    const key = mediaKey(img);
    if (!key || seen.has(key)) return;
    seen.add(key);
    if (files[key] == null) return;
    matched += 1;
    total += files[key];
  });
  if (!(product?.images || []).length) return 0;
  return matched ? total : null;
};

const AdminProductsPage = () => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTextileProduct, setSelectedTextileProduct] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [createdByStats, setCreatedByStats] = useState([]);
  const [selectedAdminFilter, setSelectedAdminFilter] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [storage, setStorage] = useState(null);
  const [storageError, setStorageError] = useState('');

  // Modern In-App Delete Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: 'single', // 'single' | 'bulk'
    product: null,
    loading: false
  });

  const navigate = useNavigate();
  const { addToast } = useToast();

  useEffect(() => {
    load();
    const loadStorage = async () => {
      try {
        const data = await getStorageUsage();
        setStorage(data);
        setStorageError('');
      } catch (error) {
        console.error('Failed to load Cloudflare usage:', error);
        setStorageError(error?.response?.data?.message || 'Could not read Cloudflare usage');
      }
    };
    loadStorage();
  }, []);

  useEffect(() => {
    const loadCreatedByStats = async () => {
      try {
        const data = await getProductsCreatedByStats();
        setCreatedByStats(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading created-by stats:', error);
        setCreatedByStats([]);
      }
    };
    loadCreatedByStats();
  }, []);

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      await downloadProductsCreatedByExcel();
    } catch (error) {
      console.error('Failed to download report:', error);
      addToast('Failed to download report', 'error');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadProductsPdf(searchTerm ? { search: searchTerm } : {});
    } catch (error) {
      console.error('Failed to download PDF:', error);
      addToast('Failed to download PDF', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const load = async (page = 1, search = '') => {
    setLoading(true);

    try {
      const textileResult = await getTextileProducts({
        scope: 'admin',
        page,
        limit: 10,
        search
      });

      setProducts(
        (textileResult.products || []).map((p) => ({
          ...p,
          catalogType: "textile"
        }))
      );

      setTotalPages(textileResult.totalPages || 1);
      setCurrentPage(textileResult.currentPage || page);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSelectedProducts([]);
    setSelectedAdminFilter(null);
    load(1, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedProducts([]);
    setSelectedAdminFilter(null);
    load(1, '');
  };

  const handleFilterByAdmin = (adminName) => {
    if (selectedAdminFilter === adminName) {
      setSelectedAdminFilter(null);
      setSearchTerm('');
      load(1, '');
    } else {
      setSelectedAdminFilter(adminName);
      setSearchTerm(adminName);
      load(1, adminName);
    }
  };

  // Open single delete confirmation
  const handleOpenDelete = (product) => {
    setDeleteModal({
      isOpen: true,
      type: 'single',
      product,
      loading: false
    });
  };

  // Open bulk delete confirmation
  const handleOpenBulkDelete = () => {
    if (!selectedProducts.length) return;
    setDeleteModal({
      isOpen: true,
      type: 'bulk',
      product: null,
      loading: false
    });
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    setDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      if (deleteModal.type === 'single' && deleteModal.product) {
        await deleteTextileProduct(deleteModal.product._id);
        setSelectedProducts((prev) => prev.filter((item) => item !== deleteModal.product._id));
      } else if (deleteModal.type === 'bulk') {
        await bulkDeleteTextileProducts(selectedProducts);
        setSelectedProducts([]);
      }
      setDeleteModal({ isOpen: false, type: 'single', product: null, loading: false });
      load(currentPage, searchTerm);
    } catch (error) {
      console.error('Delete failed:', error);
      addToast('Failed to delete product', 'error');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleView = async (id) => {
    setViewLoading(true);
    try {
      const data = await getTextileProduct(id);
      setSelectedTextileProduct(data);
    } catch (error) {
      console.error('Failed to load product details:', error);
      addToast('Failed to load product details', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  const handleSelectProduct = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const isAllCurrentPageSelected =
    products.length > 0 && products.every((p) => selectedProducts.includes(p._id));

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageIds = products.map((p) => p._id);
      setSelectedProducts((prev) => Array.from(new Set([...prev, ...currentPageIds])));
    } else {
      const currentPageIds = new Set(products.map((p) => p._id));
      setSelectedProducts((prev) => prev.filter((id) => !currentPageIds.has(id)));
    }
  };

  // Build a size -> variant lookup for the size-matrix table in drawer and table
  const getSizeMatrix = (variants = []) => {
    const bySize = {};
    variants.forEach((v) => {
      bySize[v.size] = v;
    });

    const extraSizes = Object.keys(bySize).filter((s) => !SIZE_ORDER.includes(s));
    const columns = [...SIZE_ORDER, ...extraSizes].filter((s) => bySize[s] !== undefined || SIZE_ORDER.includes(s));

    return { bySize, columns };
  };

  // ---- react-table column definitions ----
  const columns = useMemo(
    () => [
      {
        Header: 'Product',
        id: 'product',
        accessor: 'name',
        Cell: ({ row }) => {
          const p = row.original;
          return (
            <div className="apl-product-cell">
              {p.images?.[0] ? (
                <img
                  src={mediaUrl(p.images[0])}
                  alt={p.name}
                  className="apl-thumb"
                  loading="lazy"
                />
              ) : (
                <div className="apl-thumb-placeholder">
                  <Package size={20} />
                </div>
              )}
              <div className="apl-product-info">
                <strong className="apl-product-name" title={p.name}>{p.name}</strong>
                <div className="apl-category-path" title={[p.category, p.subCategory, p.productType, p.sleeveOrStyle].filter(Boolean).join(' / ')}>
                  {[p.category, p.subCategory, p.productType, p.sleeveOrStyle].filter(Boolean).join(' / ') || 'N/A'}
                </div>
              </div>
            </div>
          );
        }
      },
      {
        Header: 'File size',
        id: 'fileSize',
        accessor: (p) => productMediaBytes(p, storage?.files) || 0,
        Cell: ({ row }) => {
          const bytes = productMediaBytes(row.original, storage?.files);
          if (!storage) return <span className="apl-size-pending">{storageError ? '—' : '…'}</span>;
          if (bytes == null) return <span className="apl-size-pending">—</span>;
          return <span className="apl-file-size">{formatBytes(bytes)}</span>;
        }
      },
      { Header: 'Material', accessor: 'material' },
      { Header: 'Brand', accessor: 'brand' },
      {
        Header: 'Color',
        id: 'color',
        Cell: ({ row }) => row.original.colors?.join(', ') || row.original.color || 'N/A'
      },
      {
        Header: 'Size / Stock',
        id: 'sizeStock',
        disableSortBy: true,
        Cell: ({ row }) => {
          const p = row.original;
          if (!(p.variants?.length)) return 'N/A';

          const { bySize, columns: sizeColumns } = getSizeMatrix(p.variants);
          return (
            <table className="apl-size-table">
              <thead>
                <tr>
                  {sizeColumns.map((size) => (
                    <th key={size}>{size}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {sizeColumns.map((size) => (
                    <td key={size} className={bySize[size] && bySize[size].stockAvailable === 0 ? 'apl-stock-zero' : ''}>
                      {bySize[size] ? bySize[size].stockAvailable : '-'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          );
        }
      },
      {
        Header: 'Stock',
        id: 'stock',
        accessor: (p) => p.totalStock || p.stock || 0
      },
      {
        Header: "Price",
        id: "price",
        Cell: ({ row }) => {
          const variants = row.original.variants || [];

          if (!variants.length) return "N/A";

          const v = variants[0];
          const retailPrices = variants.map(x => Number(x.retailPrice)).filter(n => n > 0);
          const minRetail = retailPrices.length ? Math.min(...retailPrices) : 0;
          const maxRetail = retailPrices.length ? Math.max(...retailPrices) : 0;

          const retailDisplay =
            minRetail === maxRetail
              ? `₹${minRetail}`
              : `₹${minRetail} - ₹${maxRetail}`;

          return (
            <div className="apl-price-cell">
              <div><strong className="apl-price-main">{retailDisplay}</strong></div>
              <div className="apl-price-sub">MRP: ₹{v.mrp || 0}</div>
              <div className="apl-price-sub">WS: ₹{v.wholesalePrice || 0}</div>
              <div className="apl-price-sub apl-price-faint">PP: ₹{v.purchasePrice || 0}</div>
            </div>
          );
        }
      },
      {
        Header: 'Supplier',
        id: 'supplier',
        accessor: (p) => p.supplierName || p.supplier?.name || p.variants?.[0]?.supplierName || 'N/A'
      },
      {
        Header: 'Status',
        id: 'status',
        Cell: ({ row }) => (
          <span className={`apl-status-badge apl-status-${row.original.status?.toLowerCase()}`}>
            {row.original.status}
          </span>
        )
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => {
          const p = row.original;
          return (
            <div className="apl-action-icons">
              <button
                type="button"
                className="apl-action-icon apl-view-icon"
                onClick={() => handleView(p._id)}
                title="Quick View"
              >
                <Eye size={15} />
              </button>
              <button
                type="button"
                className="apl-action-icon apl-edit-icon"
                onClick={() => navigate(`/admin/products/textile/${p._id}`)}
                title="Edit Product"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                className="apl-action-icon apl-delete-icon"
                onClick={() => handleOpenDelete(p)}
                title="Delete Product"
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        }
      },
      {
        Header: "Created By",
        id: "createdBy",
        Cell: ({ row }) => {
          const user = row.original.createdBy;

          if (!user) return "N/A";

          return (
            <div className="apl-creator">
              <strong>{user.name}</strong>
              <div className="apl-creator-email">
                {user.email}
              </div>
            </div>
          );
        }
      },
      {
        Header: "Created Date",
        id: "createdDate",
        Cell: ({ row }) => {
          return new Date(row.original.createdAt).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        }
      },
      {
        id: 'select',
        disableSortBy: true,
        Header: () => (
          <input
            type="checkbox"
            checked={isAllCurrentPageSelected}
            onChange={handleSelectAll}
            title="Select all on this page"
          />
        ),
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedProducts.includes(row.original._id)}
            onChange={() => handleSelectProduct(row.original._id)}
          />
        )
      }
    ],
    [selectedProducts, products, isAllCurrentPageSelected, navigate, storage, storageError]
  );

  const usedBytes = storage?.usedBytes || 0;
  const limitBytes = storage?.limitBytes || 10 * 1024 * 1024 * 1024;
  const storagePct = limitBytes ? Math.min(100, (usedBytes / limitBytes) * 100) : 0;
  const storageTone = storagePct >= 90 ? 'is-full' : storagePct >= 70 ? 'is-warn' : '';

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Products" />

        <div className="apl-page">
        {/* Page heading */}
        <div className="apl-heading">
          <div>
            <h1 className="apl-title">Products</h1>
            <p className="apl-subtitle">Manage the catalogue, stock by size and pricing.</p>
          </div>
          <div className="apl-heading-actions">
            {createdByStats.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  title={searchTerm ? `Exports only products matching "${searchTerm}"` : 'Exports all products'}
                  className="apl-btn-outline"
                >
                  {downloadingPdf ? (
                    <Loader2 size={15} className="apl-spin" />
                  ) : (
                    <FileText size={15} />
                  )}
                  <span>
                    {downloadingPdf
                      ? 'Preparing...'
                      : searchTerm
                        ? `PDF (filtered)`
                        : 'Download PDF'}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={handleDownloadReport}
                  disabled={downloadingReport}
                  className="apl-btn-outline"
                >
                  {downloadingReport ? (
                    <Loader2 size={15} className="apl-spin" />
                  ) : (
                    <Download size={15} />
                  )}
                  <span>{downloadingReport ? 'Preparing...' : 'Download Report'}</span>
                </button>
              </>
            )}

            <button
              type="button"
              className="apl-btn-add"
              onClick={() => navigate('/admin/products/new')}
            >
              <Plus size={16} />
              <span>Add Product</span>
            </button>
          </div>
        </div>

        <section className={`apl-storage ${storageTone}`} aria-label="Cloudflare storage">
          <div className="apl-storage-head">
            <span className="apl-storage-label">
              <HardDrive size={15} /> Cloudflare storage
            </span>
            <span className="apl-storage-figures">
              {storage
                ? `${formatBytes(usedBytes)} of ${formatBytes(limitBytes)}`
                : storageError || 'Checking Cloudflare…'}
            </span>
          </div>
          <div
            className="apl-storage-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(storagePct)}
            aria-label="Cloudflare storage used"
          >
            <div className="apl-storage-fill" style={{ width: `${storage ? storagePct : 0}%` }} />
          </div>
          <p className="apl-storage-note">
            {storage
              ? `${formatBytes(Math.max(0, limitBytes - usedBytes))} free · ${storage.objectCount.toLocaleString('en-IN')} files in the bucket. The 10 GB limit covers every photo, reel and banner.`
              : 'Product file sizes are the photos stored for that product.'}
          </p>
        </section>

        {/* Created-by filter chips */}
        {createdByStats.length > 0 && (
          <div className="apl-top-controls-card">
            <div className="apl-admin-tags-wrap">
              <span className="apl-admin-tags-label">
                <User size={14} /> Created by:
              </span>
              {createdByStats.map((admin) => {
                const isActive = selectedAdminFilter === admin.name;
                return (
                  <button
                    type="button"
                    key={admin.email || admin.name}
                    className={`apl-admin-tag-btn ${isActive ? 'active' : ''}`}
                    onClick={() => handleFilterByAdmin(admin.name)}
                    title={`Filter products created by ${admin.name}`}
                  >
                    <span className="apl-admin-tag-name">{admin.name}</span>
                    <span className="apl-admin-tag-count">{admin.totalProducts}</span>
                  </button>
                );
              })}
              {selectedAdminFilter && (
                <button
                  type="button"
                  className="apl-admin-tag-clear"
                  onClick={() => handleFilterByAdmin(selectedAdminFilter)}
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Search Toolbar & Primary Actions */}
        <div className="apl-header">
          <div className="apl-header-actions">
            <form onSubmit={handleSearch} className="apl-search-form">
              <div className="apl-search-input-wrap">
                <Search size={14} className="apl-search-icon" />
                <input
                  type="text"
                  placeholder="Search products by title, supplier, category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="apl-search-input"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="apl-search-clear"
                    title="Clear search"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>

              <button type="submit" className="apl-search-btn">
                <span>Search</span>
              </button>

              <button
                type="button"
                className="apl-btn-danger"
                onClick={handleOpenBulkDelete}
                disabled={selectedProducts.length === 0}
                title={selectedProducts.length === 0 ? "Select checkboxes to delete multiple products" : "Delete selected products"}
              >
                <Trash2 size={14} />
                <span>Delete Selected ({selectedProducts.length})</span>
              </button>
            </form>
          </div>
        </div>

        {/* Table Area */}
        {loading ? (
          <div className="apl-loading-wrapper">
            <div className="apl-progress-bar-container">
              <div className="apl-progress-bar-indeterminate" />
            </div>
            <div className="apl-loading-content">
              <Loader2 size={24} className="apl-spin apl-spin-accent" />
              <span>Loading products catalog...</span>
            </div>
          </div>
        ) : (
          <div className="apl-table-wrapper">
            <Table
              key={currentPage}
              columns={columns}
              data={products}
              show={totalPages > 1}
              color="#4a3d36"
              pagecount={{
                current_page: currentPage,
                last_page: totalPages,
              }}
              setPageno={(page) => {
                setCurrentPage(page);
                setSelectedProducts([]);
                load(page, searchTerm);
              }}
              pageno={currentPage}
            />
          </div>
        )}

        {/* Quick View Drawer */}
        {(selectedTextileProduct || viewLoading) && (
          <div className="apl-drawer-backdrop" onClick={() => setSelectedTextileProduct(null)}>
            <aside className="apl-drawer" onClick={(event) => event.stopPropagation()}>
              <div className="apl-drawer-header">
                <div>
                  <span className="apl-drawer-eyebrow">Product Details</span>
                  <h2 className="apl-drawer-title">{selectedTextileProduct?.name || 'Loading...'}</h2>
                </div>
                <button
                  type="button"
                  className="apl-drawer-close"
                  onClick={() => setSelectedTextileProduct(null)}
                  title="Close"
                  aria-label="Close drawer"
                >
                  <X size={18} />
                </button>
              </div>

              {viewLoading ? (
                <div className="apl-drawer-loading">
                  <Loader2 size={26} className="apl-spin apl-spin-accent" />
                  <p>Loading product details...</p>
                </div>
              ) : selectedTextileProduct && (
                <div className="apl-drawer-body">
                  <p className="apl-category-path">
                    {[selectedTextileProduct.category, selectedTextileProduct.subCategory, selectedTextileProduct.productType, selectedTextileProduct.sleeveOrStyle].filter(Boolean).join(' / ')}
                  </p>

                  {/* Rich Image Gallery */}
                  {selectedTextileProduct.images?.length > 0 && (
                    <div className="apl-drawer-gallery">
                      <div className="apl-drawer-main-img-wrap">
                        <img
                          src={mediaUrl(selectedTextileProduct.images[0])}
                          alt={selectedTextileProduct.name}
                          className="apl-drawer-main-img"
                        />
                      </div>
                      {selectedTextileProduct.images.length > 1 && (
                        <div className="apl-drawer-images">
                          {selectedTextileProduct.images.slice(1).map((img, idx) => (
                            <img key={idx} src={mediaUrl(img)} alt={`Preview ${idx + 2}`} className="apl-drawer-img-thumb" />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pricing Cards */}
                  {selectedTextileProduct.variants?.length > 0 && (
                    <div className="apl-drawer-prices">
                      <div className="apl-drawer-price-card">
                        <div className="label">Retail Price</div>
                        <div className="value">₹{selectedTextileProduct.variants[0]?.retailPrice || 0}</div>
                      </div>
                      <div className="apl-drawer-price-card">
                        <div className="label">MRP</div>
                        <div className="value">₹{selectedTextileProduct.variants[0]?.mrp || 0}</div>
                      </div>
                      <div className="apl-drawer-price-card">
                        <div className="label">Wholesale Price</div>
                        <div className="value">₹{selectedTextileProduct.variants[0]?.wholesalePrice || 0}</div>
                      </div>
                      <div className="apl-drawer-price-card">
                        <div className="label">Purchase Price</div>
                        <div className="value">₹{selectedTextileProduct.variants[0]?.purchasePrice || 0}</div>
                      </div>
                      <div className="apl-drawer-price-card">
                        <div className="label">GST</div>
                        <div className="value">{selectedTextileProduct.variants[0]?.gst || 0}%</div>
                      </div>
                    </div>
                  )}

                  {/* Attribute Cards (No stray focus rings) */}
                  <div className="apl-drawer-facts">
                    <div>
                      <strong>Material</strong>
                      <span>{selectedTextileProduct.material || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>Brand</strong>
                      <span>{selectedTextileProduct.brand || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>Color</strong>
                      <span>{selectedTextileProduct.colors?.join(', ') || selectedTextileProduct.color || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>Status</strong>
                      <span>{selectedTextileProduct.status || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>File size</strong>
                      <span>
                        {(() => {
                          const bytes = productMediaBytes(selectedTextileProduct, storage?.files);
                          if (!storage) return storageError ? '—' : '…';
                          return bytes == null ? '—' : formatBytes(bytes);
                        })()}
                      </span>
                    </div>
                    <div>
                      <strong>Supplier</strong>
                      <span>{selectedTextileProduct.variants?.[0]?.supplierName || selectedTextileProduct.supplier?.name || 'N/A'}</span>
                    </div>
                    <div>
                      <strong>Warehouse</strong>
                      <span>{selectedTextileProduct.variants?.[0]?.warehouseName || 'N/A'}</span>
                    </div>
                  </div>

                  {selectedTextileProduct.description && (
                    <div className="apl-drawer-desc">
                      <strong>Description</strong>
                      <p className="apl-drawer-desc-text">
                        {selectedTextileProduct.description}
                      </p>
                    </div>
                  )}

                  {/* Size Matrix with correct XS -> S -> M -> L -> XL order */}
                  <div className="apl-drawer-section">
                    <h3 className="apl-drawer-section-title">
                      Stock by size
                    </h3>
                    {(() => {
                      const { bySize, columns: drawerColumns } = getSizeMatrix(selectedTextileProduct.variants || []);
                      return (
                        <div className="apl-variant-wrap">
                          <table className="apl-variant-table apl-size-matrix">
                            <thead>
                              <tr>
                                <th>Size</th>
                                {drawerColumns.map((size) => (
                                  <th key={size}>{size}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Stock</td>
                                {drawerColumns.map((size) => (
                                  <td key={size} className={bySize[size] && bySize[size].stockAvailable === 0 ? 'apl-stock-zero' : ''}>
                                    {bySize[size] ? bySize[size].stockAvailable : '-'}
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Sticky Drawer Footer with Action Button */}
              {selectedTextileProduct && (
                <div className="apl-drawer-footer">
                  <button
                    type="button"
                    className="apl-btn-outline"
                    onClick={() => setSelectedTextileProduct(null)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="apl-btn-primary"
                    onClick={() => navigate(`/admin/products/textile/${selectedTextileProduct._id}`)}
                  >
                    <Pencil size={14} />
                    <span>Edit Product</span>
                  </button>
                </div>
              )}
            </aside>
          </div>
        )}

        {/* Modern In-App Delete Confirmation Modal */}
        {deleteModal.isOpen && (
          <div className="apl-modal-backdrop" onClick={() => !deleteModal.loading && setDeleteModal({ isOpen: false, type: 'single', product: null, loading: false })}>
            <div className="apl-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="apl-modal-icon-wrap">
                <AlertTriangle size={22} />
              </div>
              <h3 className="apl-modal-title">
                {deleteModal.type === 'single' ? 'Delete Product?' : `Delete ${selectedProducts.length} Products?`}
              </h3>
              <p className="apl-modal-desc">
                {deleteModal.type === 'single'
                  ? `Are you sure you want to delete "${deleteModal.product?.name}"? Its variant stock and barcodes will be permanently deactivated.`
                  : `Are you sure you want to delete the ${selectedProducts.length} selected products? This action cannot be undone.`}
              </p>
              <div className="apl-modal-actions">
                <button
                  type="button"
                  className="apl-btn-outline"
                  onClick={() => setDeleteModal({ isOpen: false, type: 'single', product: null, loading: false })}
                  disabled={deleteModal.loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="apl-btn-delete-confirm"
                  onClick={handleConfirmDelete}
                  disabled={deleteModal.loading}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminProductsPage;
